import React, { useCallback, useMemo } from 'react';

import { Platform, Pressable, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { AddIcon, CheckBadgeIcon } from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { ADD_PROFILE_SELECTION_TITLE, PROFILE_NAME } from 'config/testID';

import { isPhone, isWeb } from 'utils/responsiveness';

import Text, { ETextCategory, ETextColor } from 'components/Text';

import ProfileEditLogo from '../ProfileEditLogo';
import styles from './styles';

const {
    PROFILE: { PROFILES_TYPE_STANDARD, PROFILES_TYPE_KIDS },
    ACCESSIBILITY: {
        ACCESSIBILITY_GENERAL_TAPP_TWICE,
        ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB,
        PROFILE: { ACCESSIBILITY_PROFILE_IN_USE, ACCESSIBILITY_PROFILE_EDIT },
    },
} = LANGUAGE_KEYS;

type Props = {
    id: string;
    iconName: string;
    name: string;
    isChildProfile: boolean;
    isSelected: boolean;
    index: number;
    isEditEnabled: boolean;
    onPress: (id: string) => void;
};

function ProfileItem({
    id,
    iconName,
    name,
    isChildProfile,
    isSelected,
    index,
    isEditEnabled,
    onPress,
}: Props) {
    const { t } = useTranslation();

    const handlePress = useCallback(() => {
        onPress(id);
    }, [id, onPress]);

    const renderProfileLogo = useCallback(() => {
        if (id) {
            return (
                <ProfileEditLogo iconId={iconName} isEditable={isEditEnabled} />
            );
        }

        return (
            <View style={styles.addProfileContainer}>
                <AddIcon testID="addIcon" />
            </View>
        );
    }, [isEditEnabled, id, iconName]);

    const accessibilityLabel = useMemo(
        () =>
            `${isEditEnabled ? `${t(ACCESSIBILITY_PROFILE_EDIT)} ` : ''}${
                id
                    ? `${name} ${t(
                          isChildProfile
                              ? PROFILES_TYPE_KIDS
                              : PROFILES_TYPE_STANDARD,
                      )}`
                    : t(name)
            }${
                isSelected && !isEditEnabled
                    ? ` ${t(ACCESSIBILITY_PROFILE_IN_USE)}`
                    : ''
            }${isWeb ? ` ${t(ACCESSIBILITY_GENERAL_TAPP_TWICE_WEB)}` : ''}`,
        [isEditEnabled, isSelected, id, name],
    );

    return (
        <Pressable
            style={({ pressed }) => [
                ((isPhone && !!id && index % 2 === 0 && index !== 4) ||
                    (!isPhone && !!id)) &&
                    styles.container,
                { opacity: pressed ? 0.5 : 1 },
            ]}
            onPress={handlePress}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel}
            {...(Platform.OS === 'ios' && {
                accessibilityHint: t(ACCESSIBILITY_GENERAL_TAPP_TWICE),
            })}
        >
            <View>
                {renderProfileLogo()}
                {isSelected && !isEditEnabled && (
                    <CheckBadgeIcon style={styles.selectedIcon} />
                )}
            </View>
            <Text
                category={ETextCategory.body2}
                color={ETextColor.neutral}
                testID={
                    id
                        ? `${PROFILE_NAME}_${index}`
                        : ADD_PROFILE_SELECTION_TITLE
                }
                bold
                center
                numberOfLines={1}
                style={styles.textNameWrapper}
            >
                {id ? name : t(name)}
            </Text>
            {!!id && (
                <Text
                    category={ETextCategory.caption1}
                    color={ETextColor.light_grey}
                    center
                >
                    {t(
                        isChildProfile
                            ? PROFILES_TYPE_KIDS
                            : PROFILES_TYPE_STANDARD,
                    )}
                </Text>
            )}
        </Pressable>
    );
}

export default ProfileItem;
